import {Opening} from "../../../../../../api/Openings";
import {UnitSystem} from "../../../../../../api/Projects";
import {MdFrameSpec} from "../frame/MdFrameSpec";
import {inches} from "../../const";
import {FrmVar} from "../../drawfrm/FrmVar";
import {HardwareGroupWithContent} from "../../../../../../api/Hardware";
import {EventEmitter2} from "../../util/EventEmitter2";
import {MdDoorLite, MdDoorLiteField} from "./MdDoorLite";
import {prepConfigDrawingUnits} from "../../util/DrawingUnits";
import {serFrmVar, restoreFrmVar} from "../../serialize/frmVar";
import {DoorDesign} from "../../../../../../api/elevationTool/DoorElevationData";
import {MdGlass, resolveGlass, withGlassEdit} from "../glass/MdGlass";

const liteIds = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// the standard stiles by door series: the left/right stiles, the top rail and the bottom
// rail. A hollow metal (or wood) door gets the HM values; an aluminum door is a
// narrow-stile frame.
const HM_STILE_SIDE = 6 * inches;
const HM_STILE_TOP = 7.5 * inches;
const HM_STILE_BOTTOM = 10 * inches;
const AL_STILE = 2.5 * inches;

// a lite closer to the edge than this past the stile still passes (rounding).
const STILE_EPS = inches / 64;

export function stileDefaults(doorSeries: string): {side: number; top: number; bottom: number} {
    if(doorSeries === "AL Door") return {side: AL_STILE, top: AL_STILE, bottom: AL_STILE};
    return {side: HM_STILE_SIDE, top: HM_STILE_TOP, bottom: HM_STILE_BOTTOM};
}

// a lite that crosses a stile line: the lite, the edges it crosses, and the message for
// the warnings list.
export type DoorLiteWarning = {
    liteId: string;
    leaf: "active" | "inactive";
    mark: string;
    edges: StileEdge[];
    message: string;
};

export type StileEdge = "left" | "right" | "top" | "bottom";

// how far down-right a duplicated lite sits from its source, so the copy is visible.
const duplicateNudge = 2 * inches;

export class MdDoorSpec {
    // tied to frameSpec
    activeDoorWidth = new FrmVar("doorSpec.activeDoorWidth", 30 * inches).overrideIsModified(true);
    inActiveDoorWidth = new FrmVar("doorSpec.inActiveDoorWidth", 1 * inches).overrideIsModified(true);
    doorThickness = new FrmVar("doorSpec.doorThickness", (1 + 3/4) * inches);

    topGap = new FrmVar("doorSpec.topGap", 1/4 * inches);
    bottomGap = new FrmVar("doorSpec.bottomGap", 5/8 * inches);
    actualHeight = new FrmVar("doorSpec.actualHeight", 7 * 12 * inches);
    overcut = new FrmVar("doorSpec.overcut", 0);
    undercut = new FrmVar("doorSpec.undercut", 0);

    // the stiles: the solid border of the leaf a lite must stay inside. stileSide is the
    // left and right stiles; stileTop the top rail; stileBottom the bottom rail. An
    // unmodified var holds the door series default (see stileDefaults); a modified one
    // is the user's value and travels with the design.
    stileSide = new FrmVar("doorSpec.stileSide", HM_STILE_SIDE);
    stileTop = new FrmVar("doorSpec.stileTop", HM_STILE_TOP);
    stileBottom = new FrmVar("doorSpec.stileBottom", HM_STILE_BOTTOM);

    // glass lites cut into the leaf, drawn by the door-elevation scene. The
    // active and inactive leaves of a pair keep their own lists — a lite is
    // scoped to whichever leaf was on screen when it was added (see `leaf`).
    lites: MdDoorLite[] = [];
    litesChanged = new EventEmitter2<string>("", "door.litesChanged");

    // true when fromDesign gave a lite a new id (see assignDuplicateIds), so the stored
    // design differs from the loaded one and must be saved again.
    liteIdsRepaired = false;

    // add a new lite to the given leaf, centered at its default size, and notify
    // subscribers. Returns the created lite.
    addLite(leaf: "active" | "inactive", leafWidth: number, leafHeight: number): MdDoorLite {
        const lite = new MdDoorLite(this.nextLiteId(leaf));
        lite.mark = this.nextMark();
        lite.leaf = leaf;
        lite.centerIn(leafWidth, leafHeight);
        this.lites.push(lite);
        this.litesChanged.emit("add");
        return lite;
    }

    // copy the given lites onto their own leaf, each offset down-right by a nudge so the copy
    // is visible rather than exactly under the original. The copy keeps the source's size and
    // glass but takes a fresh id + mark. Returns the new lites, for selecting them.
    duplicateLites(ids: string[]): MdDoorLite[] {
        const out: MdDoorLite[] = [];

        for(const id of ids) {
            const src = this.lites.find(l => l.id === id);
            if(!src) continue;

            // round-trip through the serialized form rather than copying the six FrmVars by
            // hand, so a new lite field is carried without touching this.
            const copy = MdDoorLite.fromData(Object.assign(src.toData(), {id: this.nextLiteId(src.leaf)}));
            copy.mark = this.nextMark();

            const {width, height} = this.leafSize(copy.leaf);
            copy.moveTo(src.offsetLeft.get() + duplicateNudge, src.offsetTop.get() + duplicateNudge, width, height);

            this.lites.push(copy);
            out.push(copy);
        }

        if(out.length > 0) this.litesChanged.emit("add");
        return out;
    }

    // an id no live lite holds. The counter-based scheme can repeat once a lite has been
    // removed, so it steps until the id is free.
    private nextLiteId(leaf: "active" | "inactive"): string {
        const used = new Set(this.lites.map(l => l.id));

        for(let i = this.lites.length; ; i++) {
            const id = leaf.charAt(0) + liteIds.charAt(i % liteIds.length) + i;
            if(!used.has(id)) return id;
        }
    }

    // the lowest unused mark. A removed lite frees its number for the next one added; a
    // lite that already has a mark never renumbers, so a reference to it stays valid.
    private nextMark(): string {
        const used = new Set(this.lites.map(l => l.mark));

        for(let i = 1; ; i++) {
            const mark = "G" + i;
            if(!used.has(mark)) return mark;
        }
    }

    // give a fresh id to every lite whose id an earlier lite holds — blobs saved while addLite
    // could repeat an id. In stored order, so the first holder keeps its id.
    private assignDuplicateIds() {
        const seen = new Set<string>();

        // replaced in place, so nextLiteId sees the ids already reassigned.
        this.lites.forEach((lite, i) => {
            if(seen.has(lite.id)) {
                this.lites[i] = MdDoorLite.fromData(Object.assign(lite.toData(), {id: this.nextLiteId(lite.leaf)}));
                this.liteIdsRepaired = true;
            }
            seen.add(this.lites[i].id);
        });
    }

    // give a mark to every lite that has none — blobs written before marks existed. In
    // stored order, so an old design numbers the same way a new one would.
    private assignMissingMarks() {
        for(const lite of this.lites) {
            if(!lite.mark) lite.mark = this.nextMark();
        }
    }

    // the lites belonging to one leaf, in insertion order.
    litesFor(leaf: "active" | "inactive"): MdDoorLite[] {
        return this.lites.filter(l => l.leaf === leaf);
    }

    removeLite(id: string) {
        this.lites = this.lites.filter(l => l.id !== id);
        this.litesChanged.emit("remove");
    }

    // set a lite's glass. A bead change is applied to every lite on the same leaf:
    // the leaf has one lite kit column, so all its lites must agree on the bead
    // (see glass-config.md). The other fields stay per lite.
    setLiteGlass(id: string, glass: MdGlass | undefined) {
        const lite = this.lites.find(l => l.id === id);
        if(!lite) return;

        const prevBead = resolveGlass(lite.glass).bead;
        lite.glass = glass;

        const bead = resolveGlass(glass).bead;
        if(bead !== prevBead) {
            for(const other of this.litesFor(lite.leaf)) {
                if(other.id === id) continue;
                other.glass = withGlassEdit(other.glass, {bead});
            }
        }

        this.litesChanged.emit("glass");
    }

    // the leaf size a lite is positioned within (its axes' spans).
    private leafSize(leaf: "active" | "inactive") {
        return {
            width: leaf === "active" ? this.activeDoorWidth.get() : this.inActiveDoorWidth.get(),
            height: this.actualHeight.get(),
        };
    }

    // the stile each leaf edge keeps a lite away from.
    stileFor(edge: StileEdge): number {
        switch(edge) {
            case "left":
            case "right":
                return this.stileSide.get();
            case "top":
                return this.stileTop.get();
            case "bottom":
                return this.stileBottom.get();
        }
    }

    // the leaf edges a lite sits too close to: its offset from that edge is less than
    // the stile. Reads the lite's current offsets, so the lite must be normalized to
    // its leaf first.
    stileViolations(lite: MdDoorLite): StileEdge[] {
        const offsets: Record<StileEdge, number> = {
            left: lite.offsetLeft.get(),
            right: lite.offsetRight.get(),
            top: lite.offsetTop.get(),
            bottom: lite.offsetBottom.get(),
        };
        return (["left", "right", "top", "bottom"] as StileEdge[])
            .filter(edge => offsets[edge] < this.stileFor(edge) - STILE_EPS);
    }

    // every lite that crosses a stile line, in lite order. Each lite is resolved
    // against its leaf first. `fmt` renders a distance for the messages.
    liteWarnings(fmt: (mm: number) => string): DoorLiteWarning[] {
        const out: DoorLiteWarning[] = [];
        for(const lite of this.lites) {
            const {width, height} = this.leafSize(lite.leaf);
            lite.normalize(width, height);

            const edges = this.stileViolations(lite);
            if(edges.length === 0) continue;

            const parts = edges.map(edge => `${fmt(lite.varFor(offsetField(edge)).get())} from the ${edge} (stile ${fmt(this.stileFor(edge))})`);
            out.push({liteId: lite.id, leaf: lite.leaf, mark: lite.mark, edges, message: parts.join(", ")});
        }
        return out;
    }

    // move the selected lites so they share the given edge, holding each lite's
    // size. left/top align to the min edge among them; right/bottom to the max.
    // Lites are grouped by leaf (a pair's leaves align independently). A single
    // selected lite has nothing to align to, so it anchors instead: the edge's
    // offset + the axis size are locked (see MdDoorLite.lockToEdge).
    alignLites(ids: string[], edge: "left" | "right" | "top" | "bottom") {
        for(const [leaf, group] of this.byLeaf(ids)) {
            if(group.length === 0) continue;
            const {width: LW, height: LH} = this.leafSize(leaf);

            if(group.length === 1) {
                group[0].lockToEdge(edge, LW, LH);
                continue;
            }

            if(edge === "left") {
                const target = Math.min(...group.map(l => l.offsetLeft.get()));
                for(const l of group) l.moveTo(target, l.offsetTop.get(), LW, LH);
            } else if(edge === "right") {
                const target = Math.max(...group.map(l => l.offsetLeft.get() + l.width.get()));
                for(const l of group) l.moveTo(target - l.width.get(), l.offsetTop.get(), LW, LH);
            } else if(edge === "top") {
                const target = Math.min(...group.map(l => l.offsetTop.get()));
                for(const l of group) l.moveTo(l.offsetLeft.get(), target, LW, LH);
            } else {
                const target = Math.max(...group.map(l => l.offsetTop.get() + l.height.get()));
                for(const l of group) l.moveTo(l.offsetLeft.get(), target - l.height.get(), LW, LH);
            }
        }
        this.litesChanged.emit("layout");
    }

    // even out the spacing between the selected lites. Each axis is distributed
    // independently, anchored on that axis's extreme lites: horizontal holds the
    // left-most and right-most lites fixed; vertical holds the top-most and
    // bottom-most. The lites between the anchors get equal gaps. A lite's new left
    // and top are resolved separately, then applied together. A noop below 3 lites.
    distributeLites(ids: string[]) {
        for(const [leaf, group] of this.byLeaf(ids)) {
            if(group.length < 3) continue;
            const {width: LW, height: LH} = this.leafSize(leaf);

            const left = distributeAxis(group, l => l.offsetLeft.get(), l => l.width.get());
            const top = distributeAxis(group, l => l.offsetTop.get(), l => l.height.get());
            for(const l of group) l.moveTo(left.get(l.id)!, top.get(l.id)!, LW, LH);
        }
        this.litesChanged.emit("layout");
    }

    // the selected lites grouped by which leaf they belong to.
    private byLeaf(ids: string[]): Map<"active" | "inactive", MdDoorLite[]> {
        const idSet = new Set(ids);
        const out = new Map<"active" | "inactive", MdDoorLite[]>();
        for(const l of this.lites) {
            if(!idSet.has(l.id)) continue;
            const arr = out.get(l.leaf) ?? [];
            arr.push(l);
            out.set(l.leaf, arr);
        }
        return out;
    }

    static fromOpening(opening: Opening, hardware: HardwareGroupWithContent | null, sys: UnitSystem, frame: MdFrameSpec) {
        const spec = new MdDoorSpec();

        spec.activeDoorWidth = frame.activeDoorWidth;
        spec.inActiveDoorWidth = frame.inActiveDoorWidth;
        spec.doorThickness = frame.doorFrameProfile.doorThickness;

        // import the persisted door cuts from the hardware group's prep config. Set
        // before wiring actualHeight below, which derives from them (so its initial
        // value already reflects the imported cuts).
        const overcut = prepConfigDrawingUnits(hardware, "overCutDoor");
        if(overcut !== null) spec.overcut.setInitial(overcut);
        const undercut = prepConfigDrawingUnits(hardware, "underCutDoor");
        if(undercut !== null) spec.undercut.setInitial(undercut);

        spec.actualHeight.createCalculated((input) => {
            return input.nominalHeight - (input.overcut + input.undercut + input.topGap + input.bottomGap);
        }, {
            nominalHeight: frame.nominalHeight,
            topGap: spec.topGap,
            bottomGap: spec.bottomGap,
            overcut: spec.overcut,
            undercut: spec.undercut,
        }, "nominalHeight - (overcut + undercut + topGap + bottomGap)");

        const stiles = stileDefaults(opening.doorSeries);
        spec.stileSide.setInitial(stiles.side);
        spec.stileTop.setInitial(stiles.top);
        spec.stileBottom.setInitial(stiles.bottom);

        return spec;
    }

    // serialize the door Design fields. The shared width/thickness vars (rewired from
    // the frame) and the Calc actualHeight are omitted.
    toDesign(): DoorDesign {
        return {
            topGap: serFrmVar(this.topGap),
            bottomGap: serFrmVar(this.bottomGap),
            overcut: serFrmVar(this.overcut),
            undercut: serFrmVar(this.undercut),
            stileSide: serFrmVar(this.stileSide),
            stileTop: serFrmVar(this.stileTop),
            stileBottom: serFrmVar(this.stileBottom),
            lites: this.lites.map(l => l.toData()),
        };
    }

    // rebuild from a stored Design: fromOpening re-applies the opening dimensions and
    // re-shares the frame vars, then overlay the stored gaps/cuts and lites. overcut/
    // undercut are drw-owned, so the stored values override the prep-seeded ones.
    static fromDesign(design: DoorDesign, opening: Opening, hardware: HardwareGroupWithContent | null, sys: UnitSystem, frame: MdFrameSpec): MdDoorSpec {
        const spec = MdDoorSpec.fromOpening(opening, hardware, sys, frame);

        restoreFrmVar(spec.topGap, design.topGap);
        restoreFrmVar(spec.bottomGap, design.bottomGap);
        restoreFrmVar(spec.overcut, design.overcut);
        restoreFrmVar(spec.undercut, design.undercut);

        // only a user-set stile is carried; an unmodified one keeps this opening's series
        // default (a shared design may land on another series).
        if(design.stileSide?.mod) restoreFrmVar(spec.stileSide, design.stileSide);
        if(design.stileTop?.mod) restoreFrmVar(spec.stileTop, design.stileTop);
        if(design.stileBottom?.mod) restoreFrmVar(spec.stileBottom, design.stileBottom);

        spec.lites = design.lites.map(l => MdDoorLite.fromData(l));
        spec.assignDuplicateIds();
        spec.assignMissingMarks();
        spec.litesChanged.emit("load");

        return spec;
    }
}

// the lite offset field measured from a leaf edge.
function offsetField(edge: StileEdge): MdDoorLiteField {
    switch(edge) {
        case "left": return "offsetLeft";
        case "right": return "offsetRight";
        case "top": return "offsetTop";
        case "bottom": return "offsetBottom";
    }
}

// distribute items with equal gaps along one axis, anchored on the two extreme
// lites: the one with the smallest leading edge (left/top-most) and the one with
// the largest trailing edge (right/bottom-most) both stay put. The lites between
// them, ordered by leading edge, are placed with equal gaps so the space between
// the anchors is shared evenly. Returns each item's new leading-edge position keyed
// by id. `start` reads an item's leading edge, `size` its extent on that axis.
function distributeAxis(items: MdDoorLite[], start: (l: MdDoorLite) => number, size: (l: MdDoorLite) => number): Map<string, number> {
    let leadAnchor = items[0];
    let trailAnchor = items[0];
    for(const l of items) {
        if(start(l) < start(leadAnchor)) leadAnchor = l;
        if(start(l) + size(l) > start(trailAnchor) + size(trailAnchor)) trailAnchor = l;
    }

    const out = new Map<string, number>();
    // the anchors keep their current position.
    out.set(leadAnchor.id, start(leadAnchor));
    out.set(trailAnchor.id, start(trailAnchor));
    // one lite spans the whole extent (its edges are both extremes): nothing between.
    if(leadAnchor.id === trailAnchor.id) return out;

    const middles = items
        .filter(l => l.id !== leadAnchor.id && l.id !== trailAnchor.id)
        .sort((a, b) => start(a) - start(b));

    // equal gaps across the inner span (lead anchor's trailing edge → trail anchor's
    // leading edge), less the middle lites' own extents.
    const innerSpan = start(trailAnchor) - (start(leadAnchor) + size(leadAnchor));
    const middlesTotal = middles.reduce((sum, l) => sum + size(l), 0);
    const gap = (innerSpan - middlesTotal) / (middles.length + 1);

    let cursor = start(leadAnchor) + size(leadAnchor) + gap;
    for(const l of middles) {
        out.set(l.id, cursor);
        cursor += size(l) + gap;
    }
    return out;
}